import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import {
  getJourneyConfig,
  getManageJourneys,
  ManageJourneysResponse,
  saveJourneyConfig,
  SaveJourneyConfigInput,
  SetJourneyEnabledInput,
  setJourneyEnabled,
} from './api';
import {
  journeyConfigQueryKey,
  manageJourneysQueryKey,
  manageJourneysQueryKeyPrefix,
} from './query-keys';

import { journeyListStrings } from '../components/list-strings';
import { jobJourneyConfigQueryKeyPrefix } from '../job-triggers/query-keys';

export const useManageJourneys = (search: string, isQueryEnabled = true) => {
  return useQuery({
    queryKey: manageJourneysQueryKey(search),
    queryFn: () => getManageJourneys(search),
    enabled: isQueryEnabled,
    // Keep the previous search's results on screen while a new search
    // debounces in, rather than dropping to isLoading — G34 keeps the search
    // box (and the rest of the list) mounted through that gap.
    keepPreviousData: true,
  });
};

export type SetJourneyEnabledVariables = SetJourneyEnabledInput & {
  /** Named in the confirmation toast; not part of the request body. */
  journeyTitle: string;
};

export const useSetJourneyEnabled = () => {
  const { t } = useScopedTranslate(locale);
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ journeyTitle: _title, ...input }: SetJourneyEnabledVariables) =>
      setJourneyEnabled(input),
    // The query key now carries `search`, and a mutation has no view of
    // which search the user is currently looking at — update every cached
    // search variant under the prefix rather than a single exact key.
    onMutate: async (input: SetJourneyEnabledVariables) => {
      await queryClient.cancelQueries(manageJourneysQueryKeyPrefix);
      const previous = queryClient.getQueriesData<ManageJourneysResponse>(
        manageJourneysQueryKeyPrefix
      );
      queryClient.setQueriesData<ManageJourneysResponse>(manageJourneysQueryKeyPrefix, (current) =>
        current
          ? {
              ...current,
              results: current.results.map((journey) =>
                journey.journeyId === input.journeyId
                  ? { ...journey, isEnabled: input.isEnabled }
                  : journey
              ),
            }
          : current
      );
      return { previous };
    },
    onSuccess: (_data, variables) => {
      // Under an Enabled/Disabled filter the toggled row leaves the list it was
      // in, so without this the row just vanishes with nothing to explain it.
      showSuccess(variables.isEnabled ? t('enabledSuccess') : t('disabledSuccess'), {
        subtitle: variables.journeyTitle,
      });
    },
    onError: (_error, _input, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      showError(t('toggleError'));
    },
    onSettled: () => {
      queryClient.invalidateQueries(manageJourneysQueryKeyPrefix);
    },
  });
};

export const useJourneyConfig = (journeyId: string) => {
  return useQuery({
    queryKey: journeyConfigQueryKey(journeyId),
    queryFn: () => getJourneyConfig(journeyId),
  });
};

export type SaveJourneyConfigVariables = SaveJourneyConfigInput & {
  onSuccess?: () => void;
  onSettled?: () => void;
};

export const useSaveJourneyConfig = () => {
  const { t } = useScopedTranslate(locale);
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ onSuccess, onSettled, ...input }: SaveJourneyConfigVariables) =>
      saveJourneyConfig(input),
    onSuccess: (_data, variables) => {
      showSuccess(t('saveSuccess'));
      queryClient.invalidateQueries(journeyConfigQueryKey(variables.journeyId));
      queryClient.invalidateQueries(manageJourneysQueryKeyPrefix);
      // Every job's per-job panel resolves against this global config, so a
      // global save must invalidate all of them.
      queryClient.invalidateQueries(jobJourneyConfigQueryKeyPrefix);
      variables.onSuccess?.();
    },
    onError: () => {
      showError(t('saveConfigError'));
    },
    onSettled: (_data, _error, variables) => {
      variables.onSettled?.();
    },
  });
};

const locale = {
  en: {
    ...journeyListStrings.en,
    toggleError: 'Failed to update journey status',
    enabledSuccess: 'Journey enabled',
    disabledSuccess: 'Journey disabled',
    saveSuccess: 'Journey configuration saved',
  },
  is: {
    ...journeyListStrings.is,
    toggleError: 'Ekki tókst að uppfæra stöðu Journey-ferlisins',
    enabledSuccess: 'Journey-ferlið hefur verið virkjað',
    disabledSuccess: 'Journey-ferlið hefur verið gert óvirkt',
    saveSuccess: 'Stillingar Journey-ferlisins hafa verið vistaðar',
  },
};
